import { PrimeDebugLog } from "../../util/debugLog";
import { ApiFile } from "../../shared/interfaces";

/**
 * Fetches an `ApiFile` and caches it in the browser’s Origin Private File System (OPFS) when available,
 * otherwise falls back to an in-memory download. Progress is reported continuously, and the entire
 * operation can be cancelled via an AbortSignal.
 *
 * - **OPFS path**:
 *   1. Attempts to open an existing file in OPFS cache; if non-empty, returns it immediately.
 *   2. Otherwise, creates/writes a new OPFS file, streaming bytes from network → OPFS in a pump loop.
 * - **Fallback**:
 *   Streams bytes into memory, constructs a `File` object at completion.
 *
 * Both modes:
 *   - Report byte-level progress via `onProgress(loadedBytes, totalBytes)`.
 *   - Respect `signal.abort()`, immediately halting network reads, write streams, and progress callbacks.
 *
 * @param file File to load.
 * @param onProgress
 *   Callback invoked after each chunk is processed:
 *   - `loadedBytes`: number of bytes downloaded so far
 *   - `totalBytes`: total size in bytes (zero if unknown)
 * @param signal When signaled, the fetch+write loop aborts and the promise resolves `null`.
 * @returns The cached or newly downloaded `File` object, or null if the download was aborted or if an error occurred.
 */

export async function loadFileAndCache(
    file: ApiFile,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    { signal }: { signal?: AbortSignal },
): Promise<File | null> {
    const supportsOPFS =
        typeof navigator.storage?.getDirectory === "function" &&
        typeof FileSystemFileHandle !== "undefined";

    if (supportsOPFS) {
        try {
            const opfsFile = await loadViaOpfs(file, onProgress, signal);
            if (opfsFile) return opfsFile;
        } catch (err: any) {
            if (err.name === "AbortError") {
                PrimeDebugLog("loadFileAndCache", "OPFS load aborted");
                return null;
            }
            PrimeDebugLog("loadFileAndCache", "OPFS failed — falling back", err);
        }
    } else {
        PrimeDebugLog("loadFileAndCache", "OPFS unavailable — using memory");
    }

    // OPFS unsupported or failed: do memory fetch
    return fetchToMemory(file, onProgress, signal);
}

/**
 * Clears the OPFS cache by removing all files in the origin-private root directory.
 */
export async function clearFileCache(): Promise<void> {
    // Ask for persistent quota (not strictly required for removals, but good practice)
    await navigator?.storage?.persist?.();

    // Get the origin-private root directory
    const root = await navigator.storage.getDirectory();

    // @ts-ignore: TS lib doesn't yet know about .entries()
    for await (const [name] of root.entries()) {
        try {
            await root.removeEntry(name, { recursive: true });
            PrimeDebugLog("clearFileCache", `Removed ${name}`);
        } catch (err) {
            PrimeDebugLog("clearFileCache", `Failed to remove ${name}`);
        }
    }
}

/**
 * OPFS‐based loader. Returns null on any non‐AbortError failure.
 * Throws only if `signal` is aborted.
 */
async function loadViaOpfs(
    file: ApiFile,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    signal?: AbortSignal,
): Promise<File | null> {
    await navigator.storage.persist?.();
    const root = await navigator.storage.getDirectory();
    const safeName = encodeURIComponent(`${file.id}-${file.fileName}`);

    // Try cache‐hit; swallow errors as “miss”
    const existingHandle = await root.getFileHandle(safeName, { create: false }).catch(() => null);
    if (existingHandle) {
        const cached = await existingHandle.getFile();
        if (cached.size > 0) {
            onProgress(cached.size, cached.size);
            return cached;
        }
        PrimeDebugLog("loadViaOpfs", "Empty cache → re-download");
    } else {
        PrimeDebugLog("loadViaOpfs", "Cache miss → download");
    }

    // Prepare new file handle
    const handle = await root.getFileHandle(safeName, { create: true });
    // @ts-ignore -- OPFS not fully in TS yet
    if (typeof handle.createWritable !== "function") {
        PrimeDebugLog("loadViaOpfs", "No OPFS write → memory fallback");
        return null;
    }

    // Start fetch
    const resp = await fetch(file.fileUrl, { mode: "cors", signal });
    if (!resp.ok || !resp.body) {
        return null;
    }

    const totalBytes = Number(resp.headers.get("Content-Length") || 0);
    let loadedBytes = 0;

    // Set up reader + writer
    const reader = resp.body.getReader();
    // @ts-ignore -- OPFS not fully in TS yet
    const writable = await handle.createWritable();
    const writer = writable.getWriter();

    // Pump loop (will throw on AbortError)
    while (true) {
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        const { done, value } = await reader.read();
        if (done) break;

        loadedBytes += value.byteLength;
        onProgress(loadedBytes, totalBytes);
        await writer.write(value);
    }

    await writer.close();

    const finalFile = await handle.getFile();
    onProgress(finalFile.size, totalBytes || finalFile.size);
    return finalFile;
}

/**
 * In‐memory fallback. Returns null on failure or abort.
 */
async function fetchToMemory(
    file: ApiFile,
    onProgress: (loadedBytes: number, totalBytes: number) => void,
    signal?: AbortSignal,
): Promise<File | null> {
    const resp = await fetch(file.fileUrl, { mode: "cors", signal }).catch((err) => {
        if (err.name === "AbortError") {
            PrimeDebugLog("fetchToMemory", "Aborted");
            return null;
        }
        throw err;
    });

    if (!resp || !resp.ok || !resp.body) {
        return null;
    }

    const totalBytes = Number(resp.headers.get("Content-Length") || 0);
    let loadedBytes = 0;
    const reader = resp.body.getReader();
    const chunks: Uint8Array[] = [];

    while (true) {
        if (signal?.aborted) {
            return null;
        }

        const { done, value } = await reader.read();
        if (done) {
            break;
        }

        chunks.push(value!);
        loadedBytes += value.byteLength;
        onProgress(loadedBytes, totalBytes);
    }

    const blob = new Blob(chunks, {
        type: resp.headers.get("Content-Type") || undefined,
    });
    onProgress(loadedBytes, totalBytes || loadedBytes);
    return new File([blob], file.fileName, { type: blob.type });
}
